/**
 * WebhookTable - Tabela de webhooks usando DataTable genérico
 * Mantém funcionalidade específica: status ativo/inativo, contagem de eventos
 * Princípios: DRY (reutiliza DataTable), SRP (apenas configuração de colunas)
 */
import React, { useMemo } from 'react';
import { Webhook } from '../../../../types';
import DataTable, { Column } from '../../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction, createTestAction } from '../../../../components/shared/ActionMenu';
import { notify } from '../../../../utils/toast';

interface WebhookTableProps {
    webhooks: Webhook[];
    onEdit: (webhook: Webhook) => void;
    onDelete: (webhook: Webhook) => void;
    onDuplicate: (webhook: Webhook) => void;
    selectedIds?: Set<string>;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
}

const WebhookTable: React.FC<WebhookTableProps> = ({
    webhooks, onEdit, onDelete, onDuplicate,
    selectedIds = new Set(), onToggleSelection, onToggleAll
}) => {
    const columns: Column<Webhook>[] = useMemo(() => [
        {
            key: 'name', header: 'Nome',
            render: (webhook) => <span className="font-medium text-default">{webhook.name}</span>
        },
        {
            key: 'url', header: 'URL',
            render: (webhook) => <span className="truncate max-w-xs text-secondary">{webhook.url}</span>
        },
        {
            key: 'events', header: 'Eventos',
            render: (webhook) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-subtle text-default">
                    {webhook.events.length} eventos
                </span>
            )
        },
        {
            key: 'status', header: 'Status',
            render: (webhook) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${webhook.isActive ? 'bg-green-100 text-green-800' : 'bg-surface-subtle text-default'}`}>
                    {webhook.isActive ? 'Ativo' : 'Inativo'}
                </span>
            )
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (webhook) => (
                <ActionMenu actions={[
                    createTestAction(() => notify.success('Test call successful: 200 OK')),
                    createEditAction(() => onEdit(webhook)),
                    createDuplicateAction(() => onDuplicate(webhook)),
                    createDeleteAction(() => onDelete(webhook))
                ]} />
            )
        }
    ], [onEdit, onDuplicate, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={webhooks}
            getRowKey={(w) => w.id}
            emptyMessage="Nenhum webhook encontrado."
            selectable
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
        />
    );
};

export default WebhookTable;
